// Pristilbud (/offer-details/:offerId), i startsidens stil (planer/ny-kundesamhandling-plan.md).
// Tilbudet hører til en kunde (lages fra kundekortet) og en butikk. Varene hentes fra /produkter
// (kostpris, lager og pris), og kalkylen regnes som deler i WorkshopDetails (tilbud/kalkyle.js).
// Lenke, e-post (fra butikken) og SMS sendes via /offers-rutene, som logger og setter status.
// Bare utkast kan endres. Et sendt tilbud er låst; endringer gjøres i en ny versjon med ny lenke
// («Lag ny versjon», POST /offers/:id/ny-versjon, src/lib/tilbudStatus.js, vinnsel 02.11.2026).
//
// Siden henter tilbudet på nytt hvert 30. sekund for meldinger, åpninger og godkjenning, men
// rører ikke det den ansatte redigerer (før ble ulagrede endringer overskrevet).
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import SmsTilKunden from "./components/SmsTilKunden";
import VareLinjer from "./tilbud/VareLinjer";
import ForhandsvisTilbud from "./tilbud/ForhandsvisTilbud";
import { skrivUtPrisoversikt } from "./tilbud/prisoversikt";
import { meldSmsSendt } from "./utils/smsStatus";
import { Tilbake, Sidetopp, Tekstfelt, Valgknapper, knappPrimar, knappSekundar, feltKlasse } from "./ny-kundesamhandling/Skjema";
import Ikon from "./ny-kundesamhandling/Ikon";
import KundeBanner from "./components/KundeBanner";
import KlubbKontaktBanner, { KlubbMerke } from "./components/KlubbKontaktBanner";
import { oppsummering, kr, prosent, statusFor } from "./tilbud/kalkyle";
import { parTekst } from "./skiplukk/felles";
import { linjeNokkel } from "./trykk/linjer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const BUTIKKER = [
  { verdi: 1, tittel: "Sport 1 Røyken" },
  { verdi: 2, tittel: "Sport 1 Slemmestad" },
];

const fmtDato = (d) => (d ? new Date(d).toLocaleDateString("nb-NO") : "—");
const fmtDatoTid = (d) => (d ? new Date(d).toLocaleString("nb-NO", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const omDager = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

function portalUrlFor(offer) {
  if (!offer?.portal?.token) return "";
  const base = Number(offer.butikkid) === 2
    ? (process.env.REACT_APP_OFFER_PORTAL_BASE_SLEMMESTAD || "https://tilbud.sport1slemmestad.no")
    : (process.env.REACT_APP_OFFER_PORTAL_BASE_ROYKEN || "https://tilbud.sport1royken.no");
  return `${base}/tilbud/${offer.portal.token}`;
}

const smsStandard = (offer, url) =>
  `Hei, du har mottatt et tilbud${offer?.offerRef ? ` (ref. #${offer.offerRef})` : ""}. Åpne og godkjenn her: ${url}`;

// Rabatt i tilbudet kunden får: «-10 %» eller «−600 kr» (vinnsel, 28.10).
const RABATTVISNING = [
  { verdi: "prosent", tittel: "Prosent (-10 %)" },
  { verdi: "kr", tittel: "Kroner (−600 kr)" },
];

// Det skjemaet redigerer, fra tilbudet på serveren.
const tilSkjema = (o) => ({
  linjer: (o?.produkter || []).map((l) => ({ ...l, rabattKr: l.rabattKr ?? l.totalRabattKr ?? 0 })),
  kommentar: o?.kommentar || "",
  utloper: o?.utloper ? String(o.utloper).slice(0, 10) : "",
  butikkid: Number(o?.butikkid) || 0,
  rabattVisning: o?.rabattVisning === "kr" ? "kr" : "prosent",
});

async function json(r, standard) {
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.message || standard);
  return body;
}

function Seksjon({ id, tittel, children, hoyre, className = "" }) {
  return (
    <section aria-labelledby={id} className={`space-y-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-3 ${className}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 id={id} className="text-base font-bold text-slate-900">{tittel}</h2>
        {hoyre && <div className="ml-auto">{hoyre}</div>}
      </div>
      {children}
    </section>
  );
}

// Ordren eller skiplukket tilbudet gjelder (Offer.ordreId): parene er huskelisten når skiene plukkes.
// «Pris til kunden» på ordren er tilbudets grunnlag (vinnsel, 30.10): har tilbudet andre linjer,
// kan prisene hentes fra ordren her, så de ikke må føres inn to ganger.
function FraOrdre({ ordreId, linjer, laast, onHent }) {
  const [ordre, setOrdre] = useState(null);
  useEffect(() => {
    let aktiv = true;
    fetch(`${API_BASE_URL}/orders/${ordreId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((o) => { if (aktiv) setOrdre(o); })
      .catch(() => {});
    return () => { aktiv = false; };
  }, [ordreId]);
  if (!ordre) return null;
  const skiplukk = ordre.type === "skiplukk";
  const ordrePriser = (Array.isArray(ordre.prislinjer) ? ordre.prislinjer : [])
    .map((l) => ({ ...l, rabattKr: l.rabattKr ?? l.totalRabattKr ?? 0 }));
  const ulike = ordrePriser.length > 0
    && JSON.stringify(ordrePriser.map(linjeNokkel)) !== JSON.stringify((linjer || []).map(linjeNokkel));
  return (
    <section aria-labelledby="tb-fra" className="space-y-2 rounded-2xl border-2 border-teal-200 bg-teal-50 p-3 sm:p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="tb-fra" className="text-base font-bold text-slate-900">
          {skiplukk ? `Tilbud på skiplukk nr. ${ordre.ordreid}` : `Tilbud på ordre nr. ${ordre.ordreid}`}
        </h2>
        <Link to={`/order-details/${ordre._id}`} className="ml-auto inline-flex items-center gap-1 font-semibold text-indigo-700 hover:text-indigo-900">
          Åpne {skiplukk ? "skiplukket" : "ordren"} <Ikon navn="pil" className="w-5 h-5" />
        </Link>
      </div>
      {skiplukk ? (
        <ul className="space-y-1 text-slate-800">
          {(ordre.skiplukk?.par || []).map((p, i) => (
            <li key={i} className="flex items-start gap-2 [overflow-wrap:anywhere]">
              <Ikon navn="snofnugg" className="mt-1 h-4 w-4 shrink-0 text-teal-700" />
              <span>{parTekst(p)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-slate-800 [overflow-wrap:anywhere]">
          {[ordre.Varemerke, ordre.Produkt, ordre.Størrelse && `str. ${ordre.Størrelse}`, ordre.Farge].filter(Boolean).join(", ")}
        </p>
      )}
      {ordre.Kommentar && <p className="text-sm text-slate-700 [overflow-wrap:anywhere]">Beskjed: {ordre.Kommentar}</p>}
      <ServiceLenker ids={ordre.serviceIds} />
      {ulike && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-300 bg-white px-3 py-1.5">
          <p className="min-w-0 flex-1 basis-56 text-amber-950">
            «Pris til kunden» på {skiplukk ? "skiplukket" : "ordren"} har andre linjer enn tilbudet ({ordrePriser.length} {ordrePriser.length === 1 ? "linje" : "linjer"}).
          </p>
          {laast ? (
            <p className="basis-full text-sm text-slate-700">Tilbudet er sendt og låst. Lag en ny versjon og hent prisene der.</p>
          ) : (
            <button type="button" onClick={() => onHent(ordrePriser)} className={knappSekundar}>
              <Ikon navn="import" className="w-5 h-5" /> Hent prisene fra {skiplukk ? "skiplukket" : "ordren"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// Servicer varene er koblet til (WorkshopService.butikkvarer, src/lib/butikkvarer.js). Merk
// linjer med verkstedarbeid («Verkstedarbeid»): de føres på servicen, ikke som butikksalg.
function ServiceLenker({ ids }) {
  if (!Array.isArray(ids) || !ids.length) return null;
  return (
    <p className="text-sm text-slate-800">
      Koblet til service:{" "}
      {ids.map((id, i) => (
        <React.Fragment key={id}>
          {i > 0 && ", "}
          <Link to={`/workshop/${id}`} className="font-semibold text-indigo-700 underline underline-offset-2">åpne service{ids.length > 1 ? ` ${i + 1}` : ""}</Link>
        </React.Fragment>
      ))}
      . Linjer merket «Verkstedarbeid» føres på servicen.
    </p>
  );
}

// «✓ Kunden har åpnet tilbudet 2 ganger, sist …» · «Forhåndsvist av butikken 1 gang».
function LenkeApnet({ portal }) {
  const n = Number(portal?.openCount) || 0;
  const forh = Number(portal?.previewCount) || 0;
  const sist = portal?.lastOpenedAt || portal?.lastVisited;
  return (
    <>
      {n ? (
        <span className="font-medium text-emerald-800">
          <span aria-hidden="true">✓ </span>Kunden har åpnet tilbudet {n} {n === 1 ? "gang" : "ganger"}{sist ? `, sist ${fmtDatoTid(sist)}` : ""}
        </span>
      ) : (
        <span className="text-slate-700">Kunden har ikke åpnet tilbudet ennå.</span>
      )}
      {forh > 0 && (
        <span className="text-sm text-slate-600">
          · Forhåndsvist av butikken {forh} {forh === 1 ? "gang" : "ganger"}{portal?.lastPreviewedAt ? `, sist ${fmtDatoTid(portal.lastPreviewedAt)}` : ""}
        </span>
      )}
    </>
  );
}

// Logg over klikk på lenken, nyeste først: hvem (kunden eller butikken) og når, som på servicen
// (WorkshopDetails). Klikk før 28.10 er ikke merket, og kan også være butikken. IP og nettleser
// vises ikke.
function KlikkLogg({ hendelser }) {
  const liste = Array.isArray(hendelser) ? [...hendelser].reverse() : [];
  if (!liste.length) return null;
  const hvem = (ev) => (ev.source === "staff" ? "Butikk" : ev.source === "customer" ? "Kunde" : "Kunde eller butikk");
  return (
    <details className="rounded-xl border border-slate-200 bg-white">
      <summary className="cursor-pointer px-3 py-1.5 font-semibold text-indigo-800">Vis logg over klikk ({liste.length})</summary>
      <ul className="max-h-56 divide-y divide-slate-200 overflow-y-auto border-t border-slate-200 text-sm">
        {liste.map((ev, i) => (
          <li key={ev._id || i} className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5">
            <span className={ev.source === "staff" ? "text-slate-600" : "font-medium text-emerald-800"}>{hvem(ev)}</span>
            <span className="text-slate-700">{fmtDatoTid(ev.at)}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function Melding({ m }) {
  const ansatt = m.from === "employee";
  return (
    <li className={`flex ${ansatt ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 ${ansatt ? "bg-indigo-700 text-white" : "bg-slate-100 text-slate-900"}`}>
        <p className="text-xs font-semibold opacity-80">{ansatt ? (m.fromName || "Ansatt").split(/\s+/)[0] : (m.fromName || "Kunde")}</p>
        <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{m.text}</p>
        <p className="mt-1 text-right text-xs opacity-80">{fmtDatoTid(m.createdAt)}</p>
      </div>
    </li>
  );
}

export default function EditOffer() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [lagerVersjon, setLagerVersjon] = useState(false);
  const [offer, setOffer] = useState(null);
  const [lastet, setLastet] = useState(false);
  const [feil, setFeil] = useState("");
  const [melding, setMelding] = useState("");

  const [skjema, setSkjema] = useState(tilSkjema(null));
  const lagretRef = useRef(JSON.stringify(tilSkjema(null)));
  const [lagrer, setLagrer] = useState(false);

  const [sendEpost, setSendEpost] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [epost, setEpost] = useState("");
  const [smsTekst, setSmsTekst] = useState("");
  const [sender, setSender] = useState(false);
  const [sendtSvar, setSendtSvar] = useState(null);
  const [forhandsvis, setForhandsvis] = useState(false);
  const [utskriftFeil, setUtskriftFeil] = useState("");

  const [chatTekst, setChatTekst] = useState("");
  const [chatSender, setChatSender] = useState(false);

  const ansatt = Cookies.get("selectedEmployee") || "";
  const endret = JSON.stringify(skjema) !== lagretRef.current;

  const settFraServer = useCallback((o, { skjemaOgsa = false } = {}) => {
    setOffer(o);
    if (skjemaOgsa) {
      const s = tilSkjema(o);
      // Tilbud uten butikk (eldre, eller fra kundekortet før 27.10): butikken den ansatte er i.
      // Bare i utkast; et sendt tilbud er låst (ellers ville siden alltid ha «ulagrede endringer»).
      if (!s.butikkid && String(o?.status || "utkast") === "utkast") s.butikkid = Number(Cookies.get("butikkid")) === 2 ? 2 : 1;
      setSkjema(s);
      lagretRef.current = JSON.stringify({ ...s, butikkid: Number(o?.butikkid) || 0 });
      setEpost(o?.kontakt?.epost || o?.customerSnapshot?.email || "");
      const url = portalUrlFor(o);
      if (url) setSmsTekst(smsStandard(o, url));
    }
  }, []);

  const hent = useCallback(async (skjemaOgsa = false) => {
    try {
      const o = await json(await fetch(`${API_BASE_URL}/offers/${offerId}`), "Fant ikke tilbudet");
      settFraServer(o, { skjemaOgsa });
      setFeil("");
    } catch (e) {
      if (skjemaOgsa) setFeil(e.message || "Klarte ikke å hente tilbudet.");
    } finally {
      setLastet(true);
    }
  }, [offerId, settFraServer]);

  useEffect(() => { hent(true); }, [hent]);
  // Meldinger, åpninger og godkjenning; skjemaet røres ikke.
  useEffect(() => { const id = setInterval(() => hent(false), 30000); return () => clearInterval(id); }, [hent]);

  // Kundemeldinger merkes som lest når den ansatte har tilbudet oppe.
  const uleste = (offer?.messages || []).filter((m) => m.from === "customer" && !m.readAt).length;
  useEffect(() => {
    if (!offer?._id || !uleste) return;
    // Bare den som sendte tilbudet eller siste melding, merker svaret lest (src/lib/varselAnsvarlig.js).
    fetch(`${API_BASE_URL}/offers/${offer._id}/mark-messages-read`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ansatt }),
    })
      .then((r) => r.json())
      .then((b) => { if (Array.isArray(b.messages)) setOffer((o) => (o ? { ...o, messages: b.messages } : o)); })
      .catch(() => {});
  }, [offer?._id, uleste, ansatt]);

  const sum = useMemo(() => oppsummering(skjema.linjer), [skjema.linjer]);
  const status = String(offer?.status || "utkast");
  const avgjort = status === "godkjent" || status === "avslatt";
  const trukket = status === "kansellert";
  const erstattet = status === "erstattet";
  // Sendt eller avgjort: det kunden har fått, ligger fast. Bare «Gyldig til» kan endres.
  const laast = status !== "utkast";
  const kanLageVersjon = laast && !offer?.erstattetAv;
  const portalUrl = portalUrlFor(offer);
  const kontakt = offer?.kontakt || {};
  const telefon = kontakt.telefon || offer?.customerSnapshot?.phoneNumber || "";
  const kundeNavn = kontakt.navn || [offer?.customerSnapshot?.firstName, offer?.customerSnapshot?.lastName].filter(Boolean).join(" ");

  // Prisoversikt til kassa fra tilbudet (prisoversikt.js, som på ordre/skiplukk). Det som er lagret
  // skrives ut, så endringer må lagres først; alle varer må ha pris.
  const skrivUt = async () => {
    setUtskriftFeil("");
    if (endret) { setUtskriftFeil("Lagre endringene før du skriver ut."); return; }
    const linjer = offer?.produkter || [];
    if (!linjer.length) { setUtskriftFeil("Tilbudet har ingen varer."); return; }
    if (linjer.some((l) => !(Number(String(l.pris ?? "").replace(",", ".")) > 0))) { setUtskriftFeil("Sett pris på alle varene før du skriver ut."); return; }
    const godkjentDato = offer?.quote?.approved?.ok ? offer.quote.approved.at : null;
    const ok = await skrivUtPrisoversikt({
      ordre: { type: "tilbud", ordreid: offer.offerRef || "", butikkid: offer.butikkid },
      kunde: { firstName: kundeNavn, lastName: "", phoneNumber: telefon },
      linjer,
      kilde: status === "godkjent"
        ? { tilbudRef: offer.offerRef, godkjent: godkjentDato }
        : offer?.utloper ? { tekst: `Pristilbudet gjelder til ${new Date(offer.utloper).toLocaleDateString("nb-NO")}.` } : null,
    });
    if (!ok) setUtskriftFeil("Nettleseren stoppet utskriftsvinduet. Tillat sprettoppvinduer for denne siden og prøv igjen.");
  };
  const utlopt = skjema.utloper && new Date(`${skjema.utloper}T23:59:59`) < new Date();

  const lagre = async () => {
    setLagrer(true);
    setFeil("");
    setMelding("");
    try {
      const o = await json(
        await fetch(`${API_BASE_URL}/offers/${offerId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(laast ? { utloper: skjema.utloper || undefined } : {
            produkter: skjema.linjer,
            kommentar: skjema.kommentar,
            utloper: skjema.utloper || undefined,
            butikkid: skjema.butikkid || undefined,
            rabattVisning: skjema.rabattVisning,
          }),
        }),
        "Klarte ikke å lagre tilbudet."
      );
      settFraServer({ ...o, kontakt: offer?.kontakt }, { skjemaOgsa: true });
      setMelding("Tilbudet er lagret.");
      return o;
    } catch (e) {
      setFeil(e.message);
      return null;
    } finally {
      setLagrer(false);
    }
  };

  // Erstattet tilbud gjøres gjeldende igjen (POST /offers/:id/gjenopprett): versjonen som gjelder nå,
  // blir erstattet, og dette kan godkjennes og sendes på nytt med samme lenke.
  const gjenopprett = async () => {
    if (!window.confirm("Gjøre dette tilbudet gjeldende igjen? Versjonen som gjelder nå, blir erstattet, og kunden kan ikke lenger godkjenne den.")) return;
    setLagerVersjon(true);
    setFeil("");
    setMelding("");
    try {
      const r = await fetch(`${API_BASE_URL}/offers/${offerId}/gjenopprett`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ansatt }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.message || "Klarte ikke å gjøre tilbudet gjeldende igjen.");
      await hent(true);
      setSendtSvar(null);
      setMelding("Tilbudet gjelder igjen. Send det på nytt under «Send tilbudet til kunden»; kunden får samme lenke som før.");
    } catch (e) {
      setFeil(e.message);
    } finally {
      setLagerVersjon(false);
    }
  };

  // Kopi som nytt utkast; den gamle blir «erstattet» hvis kunden ikke har svart (serveren avgjør).
  const lagNyVersjon = async () => {
    if (endret) { setFeil("Lagre eller angre endringene før du lager en ny versjon."); return; }
    setLagerVersjon(true);
    setFeil("");
    setMelding("");
    try {
      const r = await fetch(`${API_BASE_URL}/offers/${offerId}/ny-versjon`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ansatt }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (b.tilbudId) { navigate(`/offer-details/${b.tilbudId}`); return; }
        throw new Error(b.message || "Klarte ikke å lage ny versjon.");
      }
      setSendtSvar(null);
      navigate(`/offer-details/${b._id}`);
      setMelding("Ny versjon er laget. Gjør endringene og send den til kunden; den får en ny lenke.");
    } catch (e) {
      setFeil(e.message);
    } finally {
      setLagerVersjon(false);
    }
  };

  const lagLenke = async (rotate = false) => {
    const b = await json(await fetch(`${API_BASE_URL}/offers/${offerId}/portal-link${rotate ? "?rotate=1" : ""}`, { method: "POST" }), "Klarte ikke å lage lenken.");
    await hent(false);
    return b.url;
  };

  const settStatus = async (ny) => {
    setFeil("");
    try {
      const o = await json(
        await fetch(`${API_BASE_URL}/offers/${offerId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: ny, ansatt }) }),
        "Klarte ikke å endre statusen."
      );
      setOffer((p) => ({ ...o, kontakt: p?.kontakt, kontaktKlubb: p?.kontaktKlubb }));
      setMelding(ny === "kansellert" ? "Tilbudet er trukket tilbake. Kunden kan ikke godkjenne det." : "Tilbudet er gjenåpnet.");
    } catch (e) {
      setFeil(e.message);
    }
  };

  const forny = async () => {
    setSkjema((s) => ({ ...s, utloper: omDager(14) }));
    setMelding("Ny gyldighetsdato er satt. Husk å lagre.");
  };

  // Lagrer først hvis noe er endret, lager lenken om den mangler, og sender e-post og/eller SMS.
  const send = async () => {
    const medSms = sendSms && Boolean(telefon);
    if (!sendEpost && !medSms) return;
    if (!skjema.linjer.length) { setFeil("Legg til minst én vare før du sender."); return; }
    const utenPris = skjema.linjer.filter((l) => !(Number(String(l.pris ?? "").replace(",", ".")) > 0));
    if (utenPris.length) { setFeil(`Sett pris på ${utenPris.map((l) => `«${l.artikkel || "vare"}»`).join(", ")} før du sender.`); return; }
    setSender(true);
    setSendtSvar(null);
    setFeil("");
    const ok = [];
    const galt = [];
    try {
      if (endret && !(await lagre())) { setSender(false); return; }
      let url = portalUrlFor(offer);
      if (!url) url = await lagLenke(false);
      if (sendEpost) {
        try {
          await json(await fetch(`${API_BASE_URL}/offers/${offerId}/send-email`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ to: epost.trim() || undefined, ansatt }),
          }), "e-posten feilet");
          ok.push("e-post");
        } catch (e) { galt.push(e.message || "e-posten feilet"); }
      }
      if (medSms) {
        try {
          await json(await fetch(`${API_BASE_URL}/offers/${offerId}/send-sms`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phoneNumber: telefon, message: smsTekst.trim() || smsStandard(offer, url), ansatt, kundeNavn }),
          }), "SMS-en feilet");
          meldSmsSendt();
          ok.push("SMS");
        } catch (e) { galt.push(e.message || "SMS-en feilet"); }
      }
      await hent(false);
      setSendtSvar({
        ok: !galt.length,
        tekst: galt.length
          ? `${ok.length ? `Sendt på ${ok.join(" og ")}, men ` : ""}${galt.join(" og ")}.`
          : `Tilbudet er sendt på ${ok.join(" og ")}.`,
      });
    } catch (e) {
      setSendtSvar({ ok: false, tekst: e.message || "Sendingen feilet." });
    } finally {
      setSender(false);
    }
  };

  const sendChat = async () => {
    if (!chatTekst.trim()) return;
    setChatSender(true);
    try {
      const b = await json(await fetch(`${API_BASE_URL}/offers/${offerId}/employee-message`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: chatTekst.trim(), fromName: ansatt.split(/\s+/)[0] || "Ansatt", ansatt }),
      }), "Klarte ikke å sende meldingen.");
      setOffer((o) => (o ? { ...o, messages: b.messages } : o));
      setChatTekst("");
    } catch (e) {
      setFeil(e.message);
    } finally {
      setChatSender(false);
    }
  };

  if (!lastet) return <p className="mx-auto max-w-7xl text-slate-700">Henter tilbudet …</p>;
  if (!offer) {
    return (
      <div className="mx-auto max-w-7xl space-y-3">
        <Tilbake til="/offer-list">Tilbake til pristilbud</Tilbake>
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil || "Fant ikke tilbudet."}</p>
      </div>
    );
  }

  const st = statusFor(status);
  const godkjenning = offer.quote?.approved || {};

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-28">
      {offer.kundeid ? <Tilbake til={`/customer-details/${offer.kundeid}`}>Tilbake til kundekortet</Tilbake>
        : offer.kontaktKlubb ? <Tilbake til={`/klubber/${offer.kontaktKlubb.klubbId}`}>Tilbake til klubben</Tilbake>
        : <Tilbake til="/offer-list">Tilbake til pristilbud</Tilbake>}

      <Sidetopp
        ikon="dokument"
        farge="betaling"
        tittel={`Pristilbud${offer.offerRef ? ` #${offer.offerRef}` : ""}${offer.versjon > 1 ? `, versjon ${offer.versjon}` : ""}`}
        undertittel={
          <span className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${st.farge}`}>{st.navn}</span>
            <span>Laget {fmtDato(offer.registrertDato)}{offer.ansatt ? ` av ${offer.ansatt}` : ""}</span>
          </span>
        }
      >
        {offer.kontaktKlubb && <KlubbMerke klubbNavn={offer.kontaktKlubb.klubbNavn} tekst="Pristilbud til klubb" />}
      </Sidetopp>

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      {status === "godkjent" && (
        <p className="flex items-start gap-2 rounded-2xl border-2 border-emerald-400 bg-emerald-50 p-3 text-emerald-950">
          <Ikon navn="hake" className="mt-0.5 w-6 h-6 shrink-0" />
          <span><strong>Kunden har godkjent tilbudet</strong> {fmtDatoTid(godkjenning.at)}{godkjenning.note ? `: «${godkjenning.note}»` : "."} Varene kan ikke endres lenger.</span>
        </p>
      )}
      {status === "avslatt" && (
        <p className="rounded-2xl border-2 border-red-300 bg-red-50 p-3 text-red-950">
          <strong>Kunden har avslått tilbudet</strong> {fmtDatoTid(godkjenning.at)}{godkjenning.note ? `: «${godkjenning.note}»` : "."}
        </p>
      )}
      {trukket && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border-2 border-slate-300 bg-slate-50 p-3 text-slate-900">
          <p className="min-w-0 flex-1"><strong>Tilbudet er trukket tilbake.</strong> Kunden ser det, men kan ikke godkjenne det.</p>
          {!offer.erstattetAv && <button type="button" onClick={() => settStatus("utkast")} className={knappSekundar}>Gjenåpne</button>}
        </div>
      )}
      {erstattet && (
        <p className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-3 text-slate-900">
          <strong>Tilbudet er erstattet av en annen versjon.</strong> Kunden ser fortsatt denne lenken, men kan ikke godkjenne den.
        </p>
      )}

      {/* Versjoner: lenker mellom gammel og ny, og «Lag ny versjon» når tilbudet er låst. */}
      {(offer.forrigeId || offer.erstattetAv || kanLageVersjon) && (
        <div className={`flex flex-wrap items-center gap-2 rounded-2xl border p-3 ${status === "sendt" ? "border-amber-300 bg-amber-50 text-amber-950" : "border-slate-200 bg-white text-slate-800"}`}>
          <div className="min-w-0 flex-1 basis-64 space-y-1">
            {status === "sendt" && (
              <p><strong>Tilbudet er sendt og låst.</strong> Skal kunden ha et endret tilbud, lag en ny versjon. Den får en ny lenke, og denne kan ikke lenger godkjennes.</p>
            )}
            {offer.erstattetAv && (
              <p><Link to={`/offer-details/${offer.erstattetAv}`} className="font-semibold text-indigo-700 underline underline-offset-2">Åpne versjonen som gjelder nå</Link></p>
            )}
            {offer.forrigeId && (
              <p className="text-sm"><Link to={`/offer-details/${offer.forrigeId}`} className="font-semibold text-indigo-700 underline underline-offset-2">Åpne forrige versjon</Link> (meldinger og godkjenning før denne versjonen ligger der)</p>
            )}
          </div>
          {kanLageVersjon && (
            <button type="button" onClick={lagNyVersjon} disabled={lagerVersjon} className={status === "sendt" ? knappPrimar : knappSekundar}>
              <Ikon navn="dokument" className="w-5 h-5" /> {lagerVersjon ? "Lager …" : "Lag ny versjon"}
            </button>
          )}
        </div>
      )}

      {/* Kunden */}
      {/* Klubbestilling: klubben og kontaktpersonen tilbudet går til (src/lib/klubbKontakt.js). */}
      {offer.kontaktKlubb ? (
        <KlubbKontaktBanner kontakt={offer.kontaktKlubb} />
      ) : (
      <KundeBanner
        kundeId={offer.kundeid}
        navn={kundeNavn}
        telefon={telefon}
        epost={kontakt.epost || offer.customerSnapshot?.email || "Ingen e-post"}
      />
      )}

      {/* Har kunden åpnet lenken? Som på servicen (WorkshopDetails); oppdateres hvert 30. sekund. */}
      {portalUrl && (
        <p className={`flex flex-wrap items-center gap-x-2 rounded-xl border px-3 py-1.5 ${offer.portal?.openCount ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
          <LenkeApnet portal={offer.portal} />
        </p>
      )}

      <SmsTilKunden telefon={telefon} />

      {offer.ordreId && (
        <FraOrdre
          ordreId={offer.ordreId}
          linjer={skjema.linjer}
          laast={laast}
          onHent={(linjer) => { setSkjema((sk) => ({ ...sk, linjer })); setMelding("Prisene er hentet fra ordren. Lagre for å bruke dem i tilbudet."); }}
        />
      )}
      {!offer.ordreId && offer.serviceIds?.length > 0 && (
        <section aria-label="Service" className="rounded-2xl border-2 border-teal-200 bg-teal-50 p-3"><ServiceLenker ids={offer.serviceIds} /></section>
      )}

      {/* Butikk */}
      <Seksjon id="tb-butikk" tittel="Butikk">
        <p className="text-slate-700">Tilbudet gis av butikken: e-posten sendes derfra, og lenken og PDF-en viser butikken.</p>
        {laast ? (
          <p className="font-semibold text-slate-800">{BUTIKKER.find((b) => b.verdi === skjema.butikkid)?.tittel || "Ingen butikk valgt"}</p>
        ) : (
          <Valgknapper id="tb-butikk-valg" etikett="Hvilken butikk gir tilbudet?" valg={BUTIKKER} verdi={skjema.butikkid} onVelg={(v) => { setSkjema((s) => ({ ...s, butikkid: v })); setMelding(""); }} />
        )}
      </Seksjon>

      {/* Varer */}
      <Seksjon id="tb-varer" tittel="Varer" hoyre={<span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-700">{skjema.linjer.length}</span>}>
        {/* Godkjent eller avslått: varene og prisene kunden svarte på, ligger fast. Si hvorfor og hva
            man gjør i stedet, så det ikke ser ut som søket har falt ut (vinnsel, 28.10). */}
        {laast && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-1.5 text-amber-950">
            <p className="font-semibold">
              {avgjort
                ? `Tilbudet er ${status === "godkjent" ? "godkjent" : "avslått"} av kunden, så varene og prisene kan ikke endres.`
                : erstattet ? "Tilbudet er erstattet av en ny versjon, så varene og prisene kan ikke endres."
                : status === "sendt" ? "Tilbudet er sendt til kunden, så varene og prisene kan ikke endres her."
                : "Tilbudet er trukket tilbake, så varene og prisene kan ikke endres."}
            </p>
            <p className="mt-1">
              {kanLageVersjon ? (
                "Lag en ny versjon (knappen øverst) for å endre det. Kunden får en ny lenke."
              ) : offer?.ordreId ? (
                <>
                  Skal kunden ha flere varer? Legg dem til under «Pris til kunden» på{" "}
                  <Link to={`/order-details/${offer.ordreId}`} className="font-semibold text-indigo-700 underline underline-offset-2">ordren</Link>
                  {" "}(«Endre priser»), så kommer de med på prisoversikten til kassa.
                </>
              ) : (
                "Skal kunden ha flere varer, lag et nytt pristilbud fra kundekortet."
              )}
            </p>
          </div>
        )}
        <VareLinjer idPrefiks="tb" linjer={skjema.linjer} laast={laast}
          onEndre={(linjer) => { setSkjema((sk) => ({ ...sk, linjer })); setMelding(""); }} />
      </Seksjon>

      {/* Oppsummering */}
      {skjema.linjer.length > 0 && (
        <section aria-labelledby="tb-sum" className="space-y-2 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-3 sm:p-3">
          <h2 id="tb-sum" className="text-base font-bold text-slate-900">Oppsummering</h2>
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="col-span-2 min-w-0 rounded-xl bg-white p-2.5 ring-2 ring-emerald-500 sm:col-span-1">
              <dt className="text-sm text-slate-600">Til kunden</dt>
              <dd className="text-xl font-bold text-slate-900">{kr(sum.total)}</dd>
            </div>
            <div className="min-w-0 rounded-xl bg-white p-2.5 ring-1 ring-slate-200">
              <dt className="text-sm text-slate-600">Rabatt</dt>
              <dd className="font-bold text-slate-900">{kr(sum.rabattKr)} · {prosent(sum.rabattProsent)}</dd>
            </div>
            <div className="min-w-0 rounded-xl bg-white p-2.5 ring-1 ring-slate-200">
              <dt className="text-sm text-slate-600">Dekningsbidrag</dt>
              <dd className={`font-bold ${sum.bidrag < 0 ? "text-red-700" : "text-slate-900"}`}>{sum.medKostpris ? kr(sum.bidrag) : "—"}</dd>
            </div>
            <div className="min-w-0 rounded-xl bg-white p-2.5 ring-1 ring-slate-200">
              <dt className="text-sm text-slate-600">Dekningsgrad</dt>
              <dd className={`font-bold ${sum.dg != null && sum.dg < 0 ? "text-red-700" : "text-slate-900"}`}>{sum.dg != null ? prosent(sum.dg) : "—"}</dd>
            </div>
          </dl>
          {sum.manglerKostpris > 0 && (
            <p className="text-sm font-medium text-amber-900">
              {sum.manglerKostpris} {sum.manglerKostpris === 1 ? "vare" : "varer"} uten kostpris er ikke med i dekningsbidraget.
            </p>
          )}
          <p className="text-sm text-slate-700">Kostpris og dekningsbidrag vises bare for ansatte, aldri for kunden.</p>
          {/* Prisoversikt til kassa (A4 med QR på EAN), som på ordre og skiplukk: for tilbud på varer
              som hentes og betales i butikken. Ikke et salgsbilag; kvitteringen lages i kassa (P4). */}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={skrivUt} className={knappSekundar}>
              <Ikon navn="kvittering" className="w-5 h-5" /> Skriv ut prisoversikt til kassa
            </button>
            {utskriftFeil && <p role="alert" className="font-medium text-red-800">{utskriftFeil}</p>}
          </div>
        </section>
      )}

      {/* Gyldighet og kommentar */}
      <Seksjon id="tb-vilkar" tittel="Gyldighet og kommentar">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor="tb-utloper" className="mb-1 block font-semibold text-slate-800">Gyldig til</label>
            <input id="tb-utloper" type="date" value={skjema.utloper} onChange={(e) => setSkjema((s) => ({ ...s, utloper: e.target.value }))}
              aria-describedby={utlopt ? "tb-utloper-varsel" : undefined}
              className={`rounded-xl border-2 px-3 py-1.5 text-base focus:outline-none focus:ring-2 focus:ring-indigo-600/30 ${utlopt ? "border-amber-500 bg-amber-50" : "border-slate-300"}`} />
          </div>
          <button type="button" onClick={forny} className={knappSekundar}>Gyldig 14 dager fra i dag</button>
        </div>
        {utlopt && <p id="tb-utloper-varsel" className="font-medium text-amber-900">Tilbudet har utløpt. Kunden kan ikke godkjenne det før du setter en ny dato og lagrer.</p>}
        {/* Hvordan rabatten vises for kunden i lenken og PDF-en (offer.rabattVisning). Låst når
            kunden har svart, så tilbudet ser ut som det kunden godkjente. */}
        {laast ? (
          <p className="text-slate-700">Rabatten vises for kunden i {skjema.rabattVisning === "kr" ? "kroner" : "prosent"}.</p>
        ) : (
          <Valgknapper id="tb-rabattvisning" etikett="Rabatten vises for kunden i" valg={RABATTVISNING}
            verdi={skjema.rabattVisning} onVelg={(v) => { setSkjema((s) => ({ ...s, rabattVisning: v })); setMelding(""); }} />
        )}
        <Tekstfelt id="tb-kommentar" etikett="Kommentar til kunden" hjelp="Vises i tilbudet kunden ser, f.eks. leveringstid eller hva som er med." flerlinjet
          value={skjema.kommentar} onChange={(e) => setSkjema((s) => ({ ...s, kommentar: e.target.value }))} maxLength={2000} readOnly={laast} />
      </Seksjon>

      {/* Send */}
      <Seksjon id="tb-send" tittel="Send tilbudet til kunden">
        {erstattet ? (
          <div className="space-y-2">
            <p className="text-slate-700">
              Tilbudet er erstattet. Skal kunden likevel kunne godkjenne dette, gjør det gjeldende igjen og send det på nytt.
              Versjonen som gjelder nå, blir da erstattet.
            </p>
            <button type="button" onClick={gjenopprett} disabled={lagerVersjon} className={knappSekundar}>
              <Ikon navn="send" className="w-5 h-5" /> {lagerVersjon ? "Gjør gjeldende …" : "Gjør gjeldende igjen"}
            </button>
          </div>
        ) : trukket ? (
          <p className="text-slate-700">Tilbudet er trukket tilbake. Gjenåpne det for å sende.</p>
        ) : (
          <>
            <p className="text-slate-700">
              Kunden får en lenke der tilbudet kan godkjennes eller avslås, og e-posten har tilbudet som PDF.
              {endret ? " Endringene lagres først." : ""}
            </p>
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={sendEpost} onChange={(e) => setSendEpost(e.target.checked)} className="mt-1 h-5 w-5" />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-slate-900">E-post med PDF fra {BUTIKKER.find((b) => b.verdi === skjema.butikkid)?.tittel || "butikken"}</span>
              </span>
            </label>
            {sendEpost && (
              <div className="pl-8">
                <label htmlFor="tb-epost" className="mb-1 block text-sm font-medium text-slate-700">E-postadresse</label>
                <input id="tb-epost" type="email" value={epost} onChange={(e) => setEpost(e.target.value)} placeholder="Kunden har ingen e-post" className={`${feltKlasse} py-1.5 text-base`} />
              </div>
            )}
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} disabled={!telefon} className="mt-1 h-5 w-5" />
              <span className="block font-semibold text-slate-900">SMS med lenken {telefon ? `til ${telefon}` : "(kunden har ikke telefon)"}</span>
            </label>
            {sendSms && telefon && (
              <div className="pl-8">
                <label htmlFor="tb-sms" className="mb-1 block text-sm font-medium text-slate-700">Tekst</label>
                <textarea id="tb-sms" rows={3} value={smsTekst} onChange={(e) => setSmsTekst(e.target.value)} placeholder="Standardteksten med lenken brukes hvis feltet er tomt."
                  className={`${feltKlasse} py-1.5 text-base`} />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setForhandsvis(true)} disabled={!skjema.linjer.length} className={knappSekundar}>
                <Ikon navn="dokument" className="w-5 h-5" /> Forhåndsvis
              </button>
              <button type="button" onClick={send} disabled={sender || (!sendEpost && !(sendSms && telefon)) || (sendEpost && !epost.trim() && !(sendSms && telefon))} className={knappPrimar}>
                <Ikon navn="send" className="w-6 h-6" /> {sender ? "Sender …" : "Send tilbudet"}
              </button>
              {sendtSvar && <p role="status" className={`font-medium ${sendtSvar.ok ? "text-emerald-800" : "text-red-700"}`}>{sendtSvar.tekst}</p>}
            </div>
          </>
        )}
      </Seksjon>

      <ForhandsvisTilbud apen={forhandsvis} onLukk={() => setForhandsvis(false)} offer={offer} skjema={skjema}
        fornavn={offer?.customerSnapshot?.firstName || ""} />

      {/* Lenken */}
      <Seksjon id="tb-lenke" tittel="Lenken til kunden">
        {portalUrl ? (
          <>
            <p className="flex flex-wrap gap-x-2 text-slate-700">
              <LenkeApnet portal={offer.portal} />
            </p>
            <KlikkLogg hendelser={offer.portal?.openEvents} />
            <div className="flex flex-wrap gap-2">
              <label htmlFor="tb-lenke-felt" className="sr-only">Lenken</label>
              <input id="tb-lenke-felt" readOnly value={portalUrl} className="min-w-0 flex-1 basis-64 rounded-xl border-2 border-slate-200 bg-slate-50 px-3 py-1.5 text-slate-800" />
              <button type="button" onClick={() => navigator.clipboard.writeText(portalUrl).then(() => setMelding("Lenken er kopiert."))} className={knappSekundar}>Kopier</button>
              {/* staffPreview=1: telles som butikkens forhåndsvisning, ikke som at kunden har åpnet. */}
              <a href={`${portalUrl}?staffPreview=1`} target="_blank" rel="noopener noreferrer" className={knappSekundar}>Åpne</a>
            </div>
            {!erstattet && <button type="button" onClick={async () => { try { await lagLenke(true); setMelding("Ny lenke er laget. Den gamle virker ikke lenger."); } catch (e) { setFeil(e.message); } }}
              className="text-sm font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900">
              Lag ny lenke (den gamle slutter å virke)
            </button>}
          </>
        ) : (
          <p className="text-slate-700">Lenken lages når du sender tilbudet.</p>
        )}
      </Seksjon>

      {/* Dialog */}
      <Seksjon id="tb-dialog" tittel="Dialog med kunden" hoyre={uleste > 0 ? <span className="rounded-full bg-red-600 px-2.5 py-0.5 text-sm font-bold text-white">{uleste} nye</span> : null}>
        {(offer.messages || []).length === 0 ? (
          <p className="text-slate-700">Ingen meldinger ennå.</p>
        ) : (
          <ul className="max-h-96 space-y-2 overflow-y-auto">{offer.messages.map((m) => <Melding key={m._id} m={m} />)}</ul>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-0 flex-1 basis-64">
            <label htmlFor="tb-chat" className="mb-1 block text-sm font-medium text-slate-700">Melding til kunden</label>
            <textarea id="tb-chat" rows={2} value={chatTekst} onChange={(e) => setChatTekst(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              className={`${feltKlasse} py-1.5 text-base`} />
          </div>
          <button type="button" onClick={sendChat} disabled={chatSender || !chatTekst.trim()} className={knappSekundar}>{chatSender ? "Sender …" : "Send"}</button>
        </div>
      </Seksjon>

      {!avgjort && !trukket && !erstattet && (
        <div className="flex justify-end">
          <button type="button" onClick={() => settStatus("kansellert")} className="font-medium text-red-800 underline underline-offset-2 hover:text-red-900">
            Trekk tilbake tilbudet
          </button>
        </div>
      )}

      {/* Lagre: alltid synlig nederst */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-3 py-1.5 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2">
          <p role="status" className={`min-w-0 flex-1 font-medium ${endret ? "text-amber-900" : "text-slate-700"}`}>
            {melding || (endret ? "Du har endringer som ikke er lagret." : `Til kunden: ${kr(sum.total)}`)}
          </p>
          <button type="button" onClick={lagre} disabled={lagrer || !endret} className={knappPrimar}>
            <Ikon navn="hake" className="w-6 h-6" /> {lagrer ? "Lagrer …" : "Lagre"}
          </button>
        </div>
      </div>
    </div>
  );
}
